import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Modal, Button, ModalControlsStyled } from '@clickcease/cc-client-components-lib'
import {
  UpdatePasswordDescription,
  UpdatePasswordStyled,
} from 'Features/userProfile/components/ChangePasswordModal/ChangePasswordModal.styled'
import { ChangePasswordInput } from 'Components/Elements/ChangePasswordInput/ChangePasswordInput'
import { ChangePasswordError } from 'Components/Elements/ChangePasswordInput/ChangePasswordInput.styled'
import { useUpdateUserPassword } from 'Features/userProfile/api/changePassword'
import { useAppStore } from 'Stores/appStoreModuleFed'

type ChangePasswordProps = {
  isOpen: boolean
  onCloseClick: () => void
}

export const ChangePasswordModal = ({ isOpen, onCloseClick }: ChangePasswordProps) => {
  const { t } = useTranslation('userProfile')
  const [validationError, setValidationError] = useState<string>('')
  const [newPassword, setNewPassword] = useState<string>('')
  const [currentPassword, setCurrentPassword] = useState<string>('')
  const [confirmPassword, setConfirmPassword] = useState<string>('')
  const { mutate, isLoading } = useUpdateUserPassword()
  const { showToaster } = useAppStore()

  const closeModal = () => {
    setValidationError('')
    setNewPassword('')
    setCurrentPassword('')
    setConfirmPassword('')
    onCloseClick()
  }

  const isFormValid = () => {
    const regex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).{8,50}$/
    if (currentPassword.length < 2) {
      setValidationError(t('change_password.please_fill_current_password'))
    } else if (!newPassword.match(regex)) {
      setValidationError(t('change_password.password_must_contain'))
      return false
    } else if (currentPassword === newPassword) {
      setValidationError(t('change_password.current_and_new_equal'))
      return false
    } else if (newPassword !== confirmPassword) {
      setValidationError(t('change_password.password_do_not_match'))
      return false
    } else {
      setValidationError('')
      return true
    }
  }

  const changePassword = () => {
    if (isFormValid()) {
      mutate(
        { currentPassword, newPassword },
        {
          onSuccess: () => {
            showToaster(t('change_password.password_updated_successfully'), 'success')
            closeModal()
          },
          onError: () => {
            setValidationError(t('change_password.current_password_wrong'))
          },
        }
      )
    }
  }

  return (
    <Modal title={t('change_password.update_password')} isOpen={isOpen} onCloseClick={closeModal}>
      <UpdatePasswordStyled>
        <UpdatePasswordDescription>
          {t('change_password.password_must_include')}
        </UpdatePasswordDescription>

        <ChangePasswordInput
          title={t('change_password.current_password')}
          password={currentPassword}
          onChange={(value) => setCurrentPassword(value)}
          placeholder={t('change_password.current_password')}
        />
        <ChangePasswordInput
          title={t('change_password.new_password')}
          password={newPassword}
          onChange={(value) => {
            setNewPassword(value)
          }}
          placeholder={t('change_password.new_password')}
        />
        <ChangePasswordInput
          title={t('change_password.confirm_password')}
          password={confirmPassword}
          onChange={(value) => setConfirmPassword(value)}
          placeholder={t('change_password.confirm_password')}
        />
        <ChangePasswordError>{validationError}</ChangePasswordError>
      </UpdatePasswordStyled>
      <ModalControlsStyled>
        <Button variant="ghost" text={t('change_password.cancel')} onClick={closeModal} />
        <Button
          loading={isLoading}
          text={t('change_password.update')}
          onClick={changePassword}
          disabled={isLoading}
        />
      </ModalControlsStyled>
    </Modal>
  )
}
